import type { ComponentChildren } from "preact";
import { useRef } from "preact/hooks";
import type { Conversation, Issue, ItemDetail, Member, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { betaLabel, docTypeOf, readsAsPdf, typeName } from "../../lib/documents.ts";
import { useEditNudge } from "../../lib/editNudge.ts";
import { useFillHeight } from "../../lib/fillHeight.ts";
import { formatBytes, formatInstant, formatRelative } from "../../lib/format.ts";
import { filesOf, imageSrc, isOwnEmail, ownDocument, type Shown, useStableUrl } from "../../lib/itemFiles.ts";
import { sideShown } from "../../lib/itemSide.ts";
import { outsideOf, spaceUrl } from "../../lib/paths.ts";
import { nameOf, reviewerName } from "../../lib/people.ts";
import { outsideRoom } from "../../lib/presence.ts";
import { bump, type Resource } from "../../lib/resource.ts";
import { canEdit } from "../../lib/roles.ts";
import { location, navigate, setQuery } from "../../lib/router.ts";
import { issueKey } from "../../lib/state.ts";
import { fileNameOf, titleOf } from "../../lib/titles.ts";
import { Person } from "../../ui/Avatar.tsx";
import { Button, Link } from "../../ui/Button.tsx";
import { SeverityLabel } from "../../ui/counts.tsx";
import { Freshness, Section } from "../../ui/misc.tsx";
import { PdfViewer } from "../../ui/pdf/PdfViewer.tsx";
import { SideToggle } from "../../ui/SideToggle.tsx";
import { LINK } from "../../ui/styles.ts";
import { Markdown } from "../comments/markdown.tsx";
import { RichText } from "../comments/richText.tsx";
import { InlineThread } from "../comments/thread.tsx";
import { KindIcon, PeekKindIcon } from "../items/icons.tsx";
import { BetaBadge } from "./betaBadge.tsx";
import { DocumentDownload, DocumentEditing, DocumentPdf, DocumentPreview, EditingActions } from "./document.tsx";
import { EmailConversation } from "./email.tsx";
import { FindingActions } from "./findingActions.tsx";
import { notesSlot, showNotes } from "./notesSlot.ts";
import { PublishNotes } from "./PublishNotes.tsx";

export function EmailBody({ space, item, shown }: { space: SpaceOut; item: ItemDetail; shown: Shown }) {
  return <EmailConversation space={space} item={item} url={useStableUrl(shown.file.sha256, shown.url)} />;
}

/**
 * A file as it can be seen here: an imported word-processing document as the PDF made of it, once there's one
 * (`onEdit` offers Edit, in Revisa's editor); another document or spreadsheet in Revisa's viewer (document.tsx);
 * else an image, a PDF (drawn by PDF.js, the same in every browser; a presentation's made one too), its text. `fill`:
 * as tall as its box (a quick look's), where the PDF's toolbar links to the original (nothing beside it does).
 */
/** An item's presence room, from inside its space or out (as a live document's: office/sync/session.ts roomOf). */
const pdfRoom = (space: SpaceOut, item: string): string => {
  const out = outsideOf(space);
  return (out ? outsideRoom(out.api) : null) ?? `item:${item}`;
};

export function Preview({
  space,
  shown,
  fill = false,
  onEdit,
  original = fill,
  view,
}: {
  space: SpaceOut;
  shown: Shown;
  fill?: boolean;
  onEdit?: (() => void) | undefined;
  /** A PDF's toolbar links to the original (nothing beside it does: a quick look's). */
  original?: boolean;
  /** How you're there, its pointers shared with the others in its room (its own file only: an attached one's room is
   *  another item's). */
  view?: string | undefined;
}) {
  const pdf = useStableUrl(`${shown.file.sha256}:pdf`, shown.pdf);
  const type = docTypeOf(shown.file.mime);
  // From outside its space, the item's own file only (its page's reads are of it alone); an attached one as it is.
  if (type && !(outsideOf(space) && shown.attached)) {
    if (pdf && readsAsPdf(type)) {
      return <DocumentPdf shown={shown} pdf={pdf} fill={fill} onEdit={onEdit} />;
    }
    return (
      <DocumentPreview
        space={space}
        shown={shown}
        fill={fill}
        onEdit={onEdit}
        fallback={<FilePreview space={space} shown={shown} fill={fill} />}
        notesPane={(p) => <PublishNotes item={shown.id} pane={p} />}
        showNotes={showNotes}
      />
    );
  }
  return (
    <FilePreview
      space={space}
      shown={shown}
      fill={fill}
      original={original}
      presence={view && !shown.attached ? { room: pdfRoom(space, shown.id), view } : undefined}
    />
  );
}

/** A file as its own preview shows it (an image, a PDF, its text), or "No preview". */
function FilePreview({
  space,
  shown,
  fill = false,
  original = fill,
  presence,
}: {
  space: SpaceOut;
  shown: Shown;
  fill?: boolean;
  original?: boolean;
  presence?: { room: string; view: string } | undefined;
}) {
  const url = useStableUrl(shown.file.sha256, shown.url);
  const { title } = shown;
  const mime = shown.file.mime;
  const image = useStableUrl(`${shown.file.sha256}:image`, imageSrc(mime, shown.url, shown.preview));
  const pdf = useStableUrl(`${shown.file.sha256}:pdf`, shown.pdf);
  if (image) {
    return (
      // biome-ignore lint/correctness/useImageSize: user files have unknown dimensions; CSS bounds the box.
      <img
        src={image}
        alt={title}
        class={`max-w-full rounded-md border border-zinc-200 ${fill ? "max-h-full self-start object-contain" : "max-h-[40rem]"}`}
      />
    );
  }
  if (url && mime === "application/pdf") {
    return <PdfViewer url={url} title={title} fill={fill} original={original} presence={presence} />;
  }
  // A presentation (or a file shown by its package's own PDF): its PDF, the toolbar linking to the original.
  if (pdf) {
    return <PdfViewer url={pdf} title={title} fill={fill} original={fill ? (url ?? false) : false} />;
  }
  if (shown.text && mime === "text/markdown") {
    // A report (an agent's, say), formatted.
    return (
      <div
        class={`overflow-auto rounded-md border border-zinc-200 bg-white p-4 ${fill ? "min-h-0 flex-1" : "max-h-[40rem]"}`}
      >
        <Markdown space={space} text={shown.text} />
      </div>
    );
  }
  return shown.text ? (
    <pre
      class={`overflow-auto rounded-md bg-zinc-50 p-3 text-xs whitespace-pre-wrap ${fill ? "min-h-0 flex-1" : "max-h-[40rem]"}`}
    >
      {shown.text}
    </pre>
  ) : (
    <p class="text-sm text-zinc-500">{t("item.noPreview")}</p>
  );
}

interface IssueProps {
  space: SpaceOut;
  members: readonly Member[] | undefined;
  item: ItemDetail;
  onChange?: (() => void) | undefined;
  /** The item's conversation: a reviewer's finding's thread opens in place. */
  conv?: Resource<Conversation> | undefined;
}

function IssueRow({ space, members, item, onChange, conv, i }: IssueProps & { i: Issue }) {
  // A reviewer's finding: those who write act on it, here or from its thread.
  const finding = i.review && i.id ? i.id : null;
  const acting = finding && onChange && canEdit(space) ? { finding, onChange } : null;
  const inline = {
    space,
    topic: { item: item.id },
    conv: finding ? conv : undefined,
    at: { review: i.review ?? undefined, finding: i.id ?? undefined },
    close: acting && { item: item.id, finding: acting.finding, asOf: item.head, onChange: acting.onChange },
  };
  return (
    <InlineThread {...inline}>
      {(toggle, thread) => (
        <li class="flex flex-col gap-1 rounded-md border border-zinc-200 bg-white p-3 text-sm">
          <span class="flex flex-wrap items-center gap-2">
            <SeverityLabel severity={i.severity} />
            {i.original_severity ? (
              <span class="text-xs text-zinc-500">
                {t("issues.adjustedFrom", { severity: t(`severity.${i.original_severity}`) })}
              </span>
            ) : null}
            {i.path ? <code class="text-xs text-zinc-500">{i.path}</code> : null}
            {acting ? (
              <span class="ml-auto">
                <FindingActions space={space} item={item.id} asOf={item.head} issue={i} onChange={acting.onChange} />
              </span>
            ) : null}
          </span>
          <IssuePlace i={i} />
          <RichText space={space} text={i.message} inline={true} class="text-zinc-800" />
          {i.reviewer ? (
            <span class="flex flex-wrap items-center gap-x-3 text-xs text-zinc-500">
              <span>
                <Person
                  name={reviewerName(members, i)}
                  id={i.reviewer}
                  size={16}
                  label={
                    i.review ? (
                      <Link href={spaceUrl(space, `/reviews/${i.review}`)} class="hover:underline">
                        {reviewerName(members, i)}
                      </Link>
                    ) : undefined
                  }
                />
                {i.rule ? ` · ${item.rules.find((r) => r.id === i.rule)?.name ?? i.rule}` : null}
              </span>
              {toggle ? <span class="ml-auto">{toggle}</span> : null}
            </span>
          ) : null}
          {thread ? <div class="mt-2">{thread}</div> : null}
        </li>
      )}
    </InlineThread>
  );
}

const PLACE_BORDERS: Record<string, string> = {
  error: "border-red-300",
  warning: "border-amber-300",
  info: "border-blue-300",
};

/**
 * A finding's place in the item's file, as comments show theirs: what it's on, quoted; with the document shown, a
 * click marks it there (its text scrolled to).
 */
function IssuePlace({ i }: { i: Issue }) {
  const at = (i.at ?? null) as Record<string, unknown> | null;
  const quote =
    typeof at?.["quote"] === "string" ? at["quote"] : typeof at?.["range"] === "string" ? at["range"] : null;
  if (!quote) {
    return null;
  }
  const cls = `line-clamp-2 border-l-2 ${PLACE_BORDERS[i.severity] ?? "border-zinc-300"} pl-2 text-left text-xs text-zinc-600`;
  const pane = notesSlot.value?.pane;
  return pane && i.id ? (
    <button type="button" class={`${cls} hover:text-zinc-900`} onClick={() => pane.onActive(`f:${i.id}`)}>
      {quote}
    </button>
  ) : (
    <p class={cls}>{quote}</p>
  );
}

function IssueList({ issues, ...p }: IssueProps & { issues: readonly Issue[] }) {
  return (
    <ul class="flex flex-col gap-2">
      {issues.map((i) => (
        <IssueRow key={issueKey(i)} {...p} i={i} />
      ))}
    </ul>
  );
}

/**
 * How a file of the File tab is shown: its preview, with Edit for a document or spreadsheet (the item's own: here, with
 * `?edit=1`; an attached one: on its own page); the item's own being edited, its editor.
 */
function ShownFile({ space, f, fill = false }: { space: SpaceOut; f: Shown; fill?: boolean }) {
  if (editingHere(space, f)) {
    return (
      <DocumentEditing
        space={space}
        shown={f}
        fill={fill}
        notesPane={(p) => <PublishNotes item={f.id} pane={p} />}
        showNotes={showNotes}
        onDone={() => {
          setQuery({ edit: null });
          bump();
        }}
      />
    );
  }
  // Filling the File tab, its download's in the tab bar (DocumentTabEnd): not in a PDF's toolbar as well.
  return <Preview space={space} shown={f} fill={fill} original={false} view={fill ? "page" : undefined} />;
}

/** Whether `f` is a document or spreadsheet you may edit. */
const editable = (space: SpaceOut, f: Shown) => canEdit(space) && !outsideOf(space) && docTypeOf(f.file.mime) !== null;

/** Whether `f` (the item's own) is being edited here (`?edit=1`). */
const editingHere = (space: SpaceOut, f: Shown) =>
  editable(space, f) && !f.attached && location.value.searchParams.get("edit") === "1";

/**
 * Edit, for a document or spreadsheet you may edit (the item's own: here, with `?edit=1`; an attached one: on its own
 * page); nothing while it's being edited (its editor has Done).
 */
function EditButton({ space, f }: { space: SpaceOut; f: Shown }) {
  const nudged = useEditNudge();
  if (!editable(space, f)) {
    return null;
  }
  if (editingHere(space, f)) {
    // Save as a version, and Done where Edit was (what its saving says is in its header).
    return <EditingActions />;
  }
  const edit = f.attached ? () => navigate(spaceUrl(space, `/items/${f.id}?edit=1`)) : () => setQuery({ edit: "1" });
  return (
    <span ref={nudged} class="inline-flex rounded-md">
      <Button size="sm" onClick={edit}>
        {t("documents.edit")}
      </Button>
    </span>
  );
}

/** The app a converted file is shown as (the Beta dialog's "as in …"). */
function appOf(type: NonNullable<ReturnType<typeof docTypeOf>>): string {
  if (type.source === "pages" || type.source === "numbers") {
    return type.source === "pages" ? "Pages" : "Numbers";
  }
  return type.kind === "doc" ? "Microsoft Word" : "Microsoft Excel";
}

/**
 * An Office file shown (or edited) in Revisa's viewer: Beta, beside its sort. Not one read as its PDF (that's as
 * Office lays it out); editing it is in Beta again.
 */
export function FileBeta({ space, f }: { space: SpaceOut; f: Shown }) {
  const type = docTypeOf(f.file.mime);
  const editing = editingHere(space, f);
  const asPdf = !editing && Boolean(f.pdf) && readsAsPdf(type);
  const label = type && !asPdf ? betaLabel(type, editing) : null;
  return label && type ? <BetaBadge label={label} app={appOf(type)} /> : null;
}

/** A file name's extension (`.xlsx`). */
const EXTENSION = /\.[A-Za-z0-9]{2,5}$/u;

/** A way to download a file: a document or spreadsheet in the formats it's exported to (DocumentDownload), any other
 *  file as its original. */
function Download({ space, f }: { space: SpaceOut; f: Shown }) {
  const type = docTypeOf(f.file.mime);
  if (type && !outsideOf(space) && (type.native || f.url)) {
    const ext = (fileNameOf(f).match(EXTENSION)?.[0] ?? "").toLowerCase();
    const office = type.kind === "doc" ? [".docx", ".docm"].includes(ext) : [".xlsx", ".xlsm"].includes(ext);
    return (
      <DocumentDownload
        space={space}
        id={f.id}
        kind={type.kind}
        native={type.native}
        original={f.url ? { url: f.url, ext: ext || t("item.download"), office } : undefined}
      />
    );
  }
  return f.url ? (
    <a href={f.url} target="_blank" rel="noopener noreferrer" class={LINK}>
      {t("item.download")}
    </a>
  ) : null;
}

/** The File tab's name when the item's own document fills it: its sort ("Word document"). */
export function DocumentTabName({ f }: { f: Shown }) {
  return <>{typeName(f.file.mime, fileNameOf(f))}</>;
}

/**
 * At the tab bar's end while the item's own document fills the File tab: its size and download (viewing), then saving and
 * Edit or Done, and the side panel's toggle (what was the row over it: the page keeps that height for the document).
 */
export function DocumentTabEnd({ space, f }: { space: SpaceOut; f: Shown }) {
  return (
    <span class="flex shrink-0 items-center gap-3 text-sm text-zinc-600">
      {/* Viewing only: while it's edited, its size and the file to download are the saved version's, not what's shown. */}
      {editingHere(space, f) ? null : (
        <>
          <span class="tabular-nums">{formatBytes(f.file.size)}</span>
          <Download space={space} f={f} />
        </>
      )}
      <span class="flex items-center gap-2">
        <EditButton space={space} f={f} />
        <SideToggle />
      </span>
    </span>
  );
}

/** The facts over a file: its kind, its name (an attached one's, a link to its item), size, and a way to download it. */
function FileFacts({ space, f, side = false }: { space: SpaceOut; f: Shown; side?: boolean }) {
  return (
    <p class="mb-3 flex flex-wrap items-center gap-3 text-sm text-zinc-600">
      {f.attached ? (
        <PeekKindIcon space={space} item={{ id: f.id, kind: "file", file: f.file, title: f.title }} />
      ) : (
        <KindIcon item={{ kind: "file", file: f.file, title: f.title }} />
      )}
      {f.attached ? (
        <Link href={spaceUrl(space, `/items/${f.id}`)} class={`${LINK} font-medium`}>
          {titleOf(f)}
        </Link>
      ) : null}
      <span class="flex min-w-0 items-center gap-2">
        <span class="truncate" title={f.file.mime}>
          {typeName(f.file.mime, fileNameOf(f))}
        </span>
        <FileBeta space={space} f={f} />
      </span>
      <span>{formatBytes(f.file.size)}</span>
      <Download space={space} f={f} />
      <span class="ml-auto flex items-center gap-2">
        <EditButton space={space} f={f} />
        {side ? <SideToggle /> : null}
      </span>
    </p>
  );
}

/**
 * An item whose own file is a document or spreadsheet: it, as tall as the window has room for, scrolling inside
 * (its attachments are under Details), as an app's canvas: the page itself doesn't scroll. Its facts and tools are in
 * the tab bar (DocumentTabEnd; the tab named after its sort).
 */
function DocumentFile({ space, f }: { space: SpaceOut; f: Shown }) {
  const box = useRef<HTMLDivElement>(null);
  useFillHeight(box);
  return (
    <section aria-label={titleOf(f)} class="flex min-h-0 flex-col">
      {/* Out to the window's edges with nothing beside it (app.css, [data-bleed]): the side panel hidden says so. */}
      <div
        ref={box}
        class="flex min-h-0 flex-col"
        data-fill=""
        data-bleed=""
        data-bleed-right={sideShown.value ? undefined : ""}
      >
        <ShownFile space={space} f={f} fill={true} />
      </div>
    </section>
  );
}

/**
 * The File tab: the item's file and the files attached to it, each with its facts, a link to the original (a document
 * of Revisa's: its bundle) and an inline preview (a document or spreadsheet, image, PDF, extracted text); attached ones
 * link to their item.
 */
export function Files({ space, item }: { space: SpaceOut; item: ItemDetail }) {
  const own = ownDocument(item);
  if (own) {
    return <DocumentFile space={space} f={own} />;
  }
  return (
    <div class="flex flex-col gap-8">
      {filesOf(item).map((f, i) =>
        // The item's own email: as a mail client shows it.
        isOwnEmail(f) ? (
          <EmailBody key={f.id} space={space} item={item} shown={f} />
        ) : (
          <section key={f.id} aria-label={titleOf(f)}>
            <FileFacts space={space} f={f} side={i === 0} />
            <ShownFile space={space} f={f} />
          </section>
        ),
      )}
    </div>
  );
}

/** Open issues grouped by source: schema violations, then reviewers' findings. */
export function Issues({
  space,
  item,
  members,
  onChange,
  conv,
  before,
}: {
  space: SpaceOut;
  item: ItemDetail;
  members: readonly Member[] | undefined;
  /** Findings can be adjusted or closed right here; called after. */
  onChange?: () => void;
  /** The item's conversation: findings' threads open in place. */
  conv?: Resource<Conversation> | undefined;
  /** First in it (its document shown: a new finding, your review not sent yet). */
  before?: ComponentChildren;
}) {
  const groups = [
    ["item.schemaIssues", item.issues.filter((i) => i.source === "schema")],
    ["item.reviewIssues", item.issues.filter((i) => i.source === "review")],
  ] as const;
  return (
    <Section title={t("item.issues", { count: item.issues.length })}>
      {before}
      {item.issues.length === 0 ? <p class="text-sm text-zinc-500">{t("item.noIssues")}</p> : null}
      {groups.map(([title, list]) =>
        list.length > 0 ? (
          <div key={title} class="mb-4">
            <h3 class="mb-2 text-sm font-medium text-zinc-700">{t(title)}</h3>
            <IssueList space={space} issues={list} members={members} item={item} onChange={onChange} conv={conv} />
          </div>
        ) : null,
      )}
    </Section>
  );
}

type ReviewRow = ItemDetail["reviews"][number];

function ReviewRowView({
  space,
  item,
  members,
  conv,
  r,
}: {
  space: SpaceOut;
  item: ItemDetail;
  members: readonly Member[] | undefined;
  conv: Resource<Conversation> | undefined;
  r: ReviewRow;
}) {
  const inline = { space, topic: { item: item.id }, conv, at: { review: r.id } };
  return (
    <InlineThread {...inline}>
      {(toggle, thread) => (
        <li class="rounded-md border border-zinc-200 bg-white p-3 text-sm">
          <span class="flex flex-wrap items-center gap-2">
            <Person
              name={reviewerName(members, r)}
              id={r.reviewer}
              label={
                <Link href={spaceUrl(space, `/reviews/${r.id}`)} class={LINK}>
                  {reviewerName(members, r)}
                </Link>
              }
            />
            <Freshness stale={r.stale_at} />
            <span class="text-xs text-zinc-500" title={formatInstant(r.created_at)}>
              {formatRelative(r.created_at)}
            </span>
          </span>
          {r.message ? <RichText space={space} text={r.message} class="mt-1 text-zinc-700" /> : null}
          <span class="mt-1 flex flex-wrap items-center gap-x-3 text-xs text-zinc-600">
            <span>{t("item.findings", { count: r.issues })}</span>
            {toggle ? <span class="ml-auto">{toggle}</span> : null}
          </span>
          {thread ? <div class="mt-2">{thread}</div> : null}
        </li>
      )}
    </InlineThread>
  );
}

/** Latest review per reviewer, with freshness (and each one's thread, in place). */
export function Reviews({
  space,
  item,
  members,
  conv,
}: {
  space: SpaceOut;
  item: ItemDetail;
  members: readonly Member[] | undefined;
  conv?: Resource<Conversation> | undefined;
}) {
  return (
    <Section title={t("item.reviews")}>
      {item.reviews.length === 0 ? <p class="text-sm text-zinc-500">{t("item.noReviews")}</p> : null}
      <ul class="flex flex-col gap-2">
        {item.reviews.map((r) => (
          <ReviewRowView key={r.id} space={space} item={item} members={members} conv={conv} r={r} />
        ))}
      </ul>
    </Section>
  );
}

/** Where a rule's reviewer stands on the item: their latest review's age, stale, or none yet. */
function RuleStatus({ item, reviewer }: { item: ItemDetail; reviewer: string }) {
  const r = item.reviews.find((x) => x.reviewer === reviewer);
  if (!r) {
    return <span class="text-amber-700">{t("item.rule.pending")}</span>;
  }
  return r.stale_at ? (
    <span class="text-amber-700">{t("item.rule.stale")}</span>
  ) : (
    <span title={formatInstant(r.created_at)}>{t("item.rule.reviewed", { when: formatRelative(r.created_at) })}</span>
  );
}

/**
 * The rules that apply to the item: each a link to its page, who reviews by it and where that review stands, and
 * the start of its instructions (the rest on its page).
 */
export function ItemRules({
  space,
  item,
  members,
}: {
  space: SpaceOut;
  item: ItemDetail;
  members: readonly Member[] | undefined;
}) {
  return item.rules.length > 0 ? (
    <Section title={t("item.rules")}>
      <ul class="flex flex-col gap-3 text-sm">
        {item.rules.map((r) => (
          <li key={r.id}>
            <Link href={spaceUrl(space, `/rules/${r.id}`)} class={`${LINK} font-medium`}>
              {r.name}
            </Link>
            <p class="mt-0.5 flex flex-wrap gap-x-1.5 text-xs text-zinc-500">
              <Person name={nameOf(members, r.reviewer)} id={r.reviewer} size={16} />
              <RuleStatus item={item} reviewer={r.reviewer} />
            </p>
            <p class="mt-1 line-clamp-2 text-zinc-600" title={r.instructions}>
              {r.instructions}
            </p>
          </li>
        ))}
      </ul>
    </Section>
  ) : null;
}
